import { useQuery } from '@tanstack/react-query';

import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { ProductRail } from '@/features/storefront/components/ProductRail';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { cn } from '@/lib/cn';

/** How many products each rail asks for. Two screens' worth of pushing. */
const RAIL_SIZE = 12;

/**
 * What the shop suggests once a shopper has read the product: others like it,
 * and others bought alongside it.
 *
 * Fetched apart from the product itself so the page a shopper came for paints
 * without waiting on a recommendation, and so a family's arrangement of the
 * product - image left, image above, image edge to edge - has no bearing on
 * where these stand. They run the full width of the page column underneath it
 * in every family.
 *
 * Nothing is rendered until the answer arrives, and nothing is rendered if it
 * never does: a suggestion is the one part of this page that is allowed to be
 * missing.
 */
export function RelatedProducts({ slug, className }: { slug: string; className?: string }) {
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const copy = useShopContent();
  const related = useQuery({
    queryKey: [...scope, 'product', slug, 'related'],
    queryFn: () => source.relatedProducts(slug, RAIL_SIZE),
  });

  const data = related.data;
  if (!data || (data.similar.length === 0 && data.also_liked.length === 0)) return null;

  return (
    <StorefrontContainer className={cn('pb-16', className)}>
      <ProductRail title={copy('product.related_similar')} products={data.similar} />
      <ProductRail title={copy('product.related_also_liked')} products={data.also_liked} />
    </StorefrontContainer>
  );
}
